/**
 * The operator console's client.
 *
 * A SEPARATE FILE, and separate from everything in `api/` that talks about a
 * project. The console is not part of the product a customer uses: it runs
 * beside it, on its own cookie, and nothing here should ever be imported by a
 * screen a customer can reach.
 */

import { request } from './client.js';

export interface OperatorMe {
  readonly id: string;
  readonly name: string;
  readonly email: string;
}

export interface CompanySummary {
  readonly tenant_id: string;
  readonly name: string;
  readonly legal_name: string | null;
  readonly created_at: string;
  readonly suspended_at: string | null;
  readonly suspended_reason: string | null;
  readonly people: number;
  readonly projects: number;
  /** Nobody has ever signed in — usually the founder never redeemed their link. */
  readonly never_used: boolean;
  readonly last_activity_at: string | null;
  readonly pending_invitations: number;
}

export interface CompanyMember {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly last_login_at: string | null;
  readonly locked: boolean;
  readonly live_sessions: number;
}

export interface CompanyDetail extends CompanySummary {
  readonly members: readonly CompanyMember[];
  readonly invitations: readonly { readonly id: string; readonly email: string; readonly expires_at: string }[];
}

export interface OperatorEvent {
  readonly action: string;
  readonly operator_name: string | null;
  readonly tenant_name: string | null;
  readonly detail: unknown;
  readonly occurred_at: string;
}

/** A link shown ONCE. Only its hash is stored, so it genuinely cannot be shown again. */
export interface OneTimeLink {
  readonly path: string;
  readonly expires_at: string;
}

export async function operatorSignIn(email: string, password: string): Promise<OperatorMe> {
  const body = await request<{ operator: OperatorMe }>('/v1/operator/session', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return body.operator;
}

export async function operatorSignOut(): Promise<void> {
  await request('/v1/operator/session', { method: 'DELETE' });
}

export async function operatorMe(): Promise<OperatorMe> {
  return (await request<{ operator: OperatorMe }>('/v1/operator/me')).operator;
}

export async function completeOperatorSetup(
  token: string,
  email: string,
  password: string,
): Promise<void> {
  await request('/v1/operator/setup', {
    method: 'POST',
    body: JSON.stringify({ token, email, password }),
  });
}

export async function listCompanies(): Promise<readonly CompanySummary[]> {
  return (await request<{ data: readonly CompanySummary[] }>('/v1/operator/companies')).data;
}

export async function describeCompany(tenantId: string): Promise<CompanyDetail> {
  const body = await request<{ data: CompanyDetail }>(
    `/v1/operator/companies/${encodeURIComponent(tenantId)}`,
  );
  return body.data;
}

export async function createCompany(input: {
  name: string;
  admin_email: string;
  legal_name?: string;
}): Promise<{
  tenant_id: string;
  name: string;
  admin_email: string;
  invitation_path: string;
  invitation_expires_at: string;
}> {
  return request('/v1/operator/companies', { method: 'POST', body: JSON.stringify(input) });
}

export async function issueResetFor(tenantId: string, userId: string): Promise<OneTimeLink> {
  return request(
    `/v1/operator/companies/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/password-reset`,
    { method: 'POST' },
  );
}

export async function unlockMember(tenantId: string, userId: string): Promise<void> {
  await request(
    `/v1/operator/companies/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/unlock`,
    { method: 'POST' },
  );
}

export async function endSessionsFor(tenantId: string, userId: string): Promise<number> {
  const body = await request<{ ended: number }>(
    `/v1/operator/companies/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/sessions`,
    { method: 'DELETE' },
  );
  return body.ended;
}

export async function setCompanySuspended(
  tenantId: string,
  suspended: boolean,
  reason?: string,
): Promise<void> {
  await request(`/v1/operator/companies/${encodeURIComponent(tenantId)}/suspension`, {
    method: 'PATCH',
    body: JSON.stringify({ suspended, ...(reason ? { reason } : {}) }),
  });
}

export async function listOperatorEvents(): Promise<readonly OperatorEvent[]> {
  return (await request<{ data: readonly OperatorEvent[] }>('/v1/operator/events')).data;
}

// --------------------------------------------------------- entering a company

/**
 * A visit: thirty minutes of looking at one customer's work, and nothing else.
 *
 * The three routes below only ever start, stop and report one. The customer's
 * actual data is read on the customers' own routes with the operator's cookie —
 * so there is nothing here that fetches a project, and there should never be.
 */
export interface SupportVisitState {
  readonly company: string;
  readonly tenant_id: string;
  readonly reason: string;
  readonly money_visible: boolean;
  readonly started_at: string;
  readonly expires_at: string;
}

export interface PastVisit {
  readonly operator_name: string | null;
  readonly reason: string;
  readonly include_money: boolean;
  readonly started_at: string;
  readonly expires_at: string;
  readonly ended_at: string | null;
  readonly ended_reason: string | null;
}

export async function enterCompany(
  tenantId: string,
  input: { reason: string; include_money?: boolean; money_reason?: string },
): Promise<SupportVisitState & { minutes: number }> {
  return request(`/v1/operator/companies/${encodeURIComponent(tenantId)}/visit`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Where the operator is, if anywhere. Null is the ordinary answer. */
export async function currentVisit(): Promise<SupportVisitState | null> {
  return (await request<{ data: SupportVisitState | null }>('/v1/operator/visit')).data;
}

/** Leaving twice is not an error, so this never throws for want of a visit. */
export async function leaveCompany(): Promise<boolean> {
  return (await request<{ left: boolean }>('/v1/operator/visit', { method: 'DELETE' })).left;
}

export async function visitsFor(tenantId: string): Promise<readonly PastVisit[]> {
  const body = await request<{ data: readonly PastVisit[] }>(
    `/v1/operator/companies/${encodeURIComponent(tenantId)}/visits`,
  );
  return body.data;
}
